import { FC, useContext, useEffect, useRef, useState } from 'react'
import { useFeedStore } from '../../../store/feed-store'
import styles from './VideoPlayer.module.css'
import { useOnScreen } from '../../../common/hooks/useOnScreen'
import { SwiperContext } from '../../../pages/Feed/Feed'
import { useSwiper, useSwiperSlide } from 'swiper/react'

interface VideoPlayerProps {
  url: string
}

const VideoPlayer: FC<VideoPlayerProps> = ({ url }) => {
  const ref = useRef<HTMLVideoElement>(null)
  const [showControls, setShowControls] = useState(false)
  const isOnScreen = useOnScreen(ref)
  const { mute, toggleMute } = useFeedStore()
  const { isActive, isNext, isPrev } = useSwiperSlide()
  const swiper = useSwiper()
  const { swiper: feedSwiper, setShowSoundControl } = useContext(SwiperContext)
  const classNames = [styles.videoPlayer]

  useEffect(() => {
    setShowSoundControl && setShowSoundControl(isOnScreen || isActive)
  }, [isActive, isOnScreen, setShowSoundControl])

  useEffect(() => {
    const beforeTransitionStart = () => {
      if (!ref.current?.paused) {
        ref.current?.pause()
      }
    }

    const init = async () => {
      if (!ref.current) {
        return
      }

      if (isOnScreen && ref.current.paused) {
        try {
          ref.current.currentTime = 0
          await ref.current.play()
        } catch (error) {
          setShowControls(true)

          if (!mute) {
            toggleMute()
            setTimeout(() => {
              ref.current?.pause()
            }, 50)
          }
        }
      }
    }

    init()

    swiper.on('beforeTransitionStart', beforeTransitionStart)
    swiper.on('transitionEnd', init)
    feedSwiper?.on('beforeTransitionStart', beforeTransitionStart)
    feedSwiper?.on('transitionEnd', init)

    return () => {
      swiper.off('beforeTransitionStart', beforeTransitionStart)
      swiper.off('transitionEnd', init)
      feedSwiper?.off('beforeTransitionStart', beforeTransitionStart)
      feedSwiper?.off('transitionEnd', init)
    }
  }, [
    feedSwiper,
    isOnScreen,
    mute,
    ref,
    setShowControls,
    setShowSoundControl,
    swiper,
    toggleMute,
  ])

  const onClick = () => {
    if (showControls) {
      setShowControls(false)

      if (mute) {
        toggleMute()
      }
    }
  }

  if (ref.current && ref.current.videoHeight < ref.current.videoWidth) {
    classNames.push(styles.landscape)
  }

  const autoPreload = isActive || isNext || isPrev

  return (
    <video
      ref={ref}
      className={classNames.join(' ')}
      autoPlay={isOnScreen && !showControls}
      onClick={onClick}
      controls={showControls}
      muted={mute}
      preload={autoPreload ? 'auto' : 'metadata'}
      loop
      playsInline
    >
      <source src={url} />
    </video>
  )
}

export default VideoPlayer
